import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { socket } from './api/socket';
import './i18n';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';

// Import pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminDashboard from './pages/admin/Dashboard';
import PostJob from './pages/student/PostJob';
import MyJobs from './pages/student/MyJobs';
import JobDetails from './pages/tutor/JobDetails';
import BrowseJobs from './pages/tutor/BrowseJobs';
import TutorMyClasses from './pages/tutor/TutorMyClasses';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ForgotPassword from './pages/ForgotPassword';

// Home Views vs Classroom Panels Allocation
import TutorHome from './pages/tutor/TutorHome';
import TutorDashboard from './pages/tutor/Dashboard';
import StudentHome from './pages/student/StudentHome';
import StudentDashboard from './pages/student/Dashboard';
import TakeExam from './pages/student/TakeExam';

/**
 * 🛰️ SESSION REDIRECT LISTENER
 * Globally listens for tutor invites for students.
 */
const SessionRedirectListener = ({ user }: { user: any }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(); 

  useEffect(() => {
    if (!user || user.role !== 'STUDENT') return;

    const handleInvite = (data: { roomId: string }) => {
      const params = new URLSearchParams(location.search);
      
      if (location.pathname.includes('/student/dashboard') && params.get('room') === data.roomId) {
        return;
      }

      if (window.confirm(t('tutor_started_session', "🚀 Your Tutor has started the session! Join now?"))) {
        navigate(`/student/dashboard?room=${data.roomId}`);
      }
    };

    socket.on('notify-student-session-start', handleInvite);
    return () => { socket.off('notify-student-session-start', handleInvite); };
  }, [user, navigate, location, t]);

  return null;
};

/**
 * 🛡️ PENDING VERIFICATION COMPONENT
 */
const PendingVerification = () => {
  const { logout } = useAuth();
  const { t } = useTranslation(); 

  return (
    <div className="h-screen-safe flex items-center justify-center bg-gray-50 px-4 text-center font-sans">
      <div className="max-w-md bg-white p-10 rounded-3xl shadow-xl border border-yellow-100">
        <div className="bg-yellow-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl animate-pulse">⏳</span>
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tighter">
          {t('verification_pending', 'Verification Pending')}
        </h2>
        <p className="text-slate-500 mb-8 leading-relaxed">
          {t('verification_desc', "Thanks for joining TutorAtrons! Our team is reviewing your credentials. You'll get full access once approved.")}
        </p>
        <button
          onClick={() => logout()}
          className="bg-slate-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-600 transition-all"
        >
          {t('return_to_login_btn', 'Return to Login')}
        </button>
      </div>
    </div>
  );
};

function App() {
  const { user, loading } = useAuth();
  const { t } = useTranslation(); 

  if (loading) {
    return (
      <div className="h-screen-safe flex flex-col items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="font-black text-blue-600 animate-pulse tracking-widest uppercase text-xs">
          {t('app_name', 'TutorAtrons')}
        </p>
      </div>
    );
  }

  const getRedirectPath = (role: string) => {
    switch (role) {
      case 'ADMIN': return "/admin";
      case 'TUTOR': return "/tutor/home";
      case 'STUDENT': return "/student/home";
      default: return "/login";
    }
  };

  return (
    <Router>
      <SessionRedirectListener user={user} />
      
      {/* 🚀 ADDED THE LANGUAGE SWITCHER HERE SO IT RENDERS ON ALL PAGES */}
      <LanguageSwitcher />

      <Routes>
        <Route path="/" element={!user ? <LandingPage /> : <Navigate to={getRedirectPath(user.role)} replace />} />
        <Route path="/login" element={!user ? <Login /> : <Navigate to={getRedirectPath(user.role)} replace />} />
        <Route path="/signup" element={!user ? <Signup /> : <Navigate to={getRedirectPath(user.role)} replace />} />
        <Route path="/pending" element={<PendingVerification />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route path="/admin" element={user?.role === 'ADMIN' ? <AdminDashboard /> : <Navigate to="/login" replace />} />

        <Route path="/tutor/home" element={user?.role === 'TUTOR' ? (user.isVerified ? <TutorHome /> : <Navigate to="/pending" replace />) : <Navigate to="/login" replace />} />
        <Route path="/tutor/dashboard" element={user?.role === 'TUTOR' ? (user.isVerified ? <TutorDashboard /> : <Navigate to="/pending" replace />) : <Navigate to="/login" replace />} />

        <Route path="/tutor/jobs/browse" element={user?.role === 'TUTOR' && user.isVerified ? <BrowseJobs /> : <Navigate to="/tutor/home" replace />} />
        <Route path="/tutor/jobs/:jobId" element={user?.role === 'TUTOR' && user.isVerified ? <JobDetails /> : <Navigate to="/tutor/home" replace />} />
        <Route path="/tutor/my-classes" element={user?.role === 'TUTOR' && user.isVerified ? <TutorMyClasses /> : <Navigate to="/tutor/home" replace />} />
        <Route path="/classroom/:roomId" element={user?.role === 'TUTOR' && user.isVerified ? <TutorMyClasses /> : <Navigate to="/tutor/home" replace />} />

        <Route path="/student/home" element={user?.role === 'STUDENT' ? <StudentHome /> : <Navigate to="/login" replace />} />
        <Route path="/student/dashboard" element={user?.role === 'STUDENT' ? <StudentDashboard /> : <Navigate to="/login" replace />} />

        <Route path="/student/post-job" element={user?.role === 'STUDENT' ? <PostJob /> : <Navigate to="/login" replace />} />
        <Route path="/student/my-jobs" element={user?.role === 'STUDENT' ? <MyJobs /> : <Navigate to="/login" replace />} />
        <Route path="/student/exam/:id" element={user?.role === 'STUDENT' ? <TakeExam /> : <Navigate to="/login" replace />} />

        <Route path="*" element={<Navigate to={user ? getRedirectPath(user.role) : "/"} replace />} />
      </Routes>
    </Router>
  );
}

export default App;